import { AbstractControl, FormArray, FormGroup } from "@angular/forms";

export function sortComparer<T, K extends keyof T>(fields: K[], order: 'asc' | 'desc' = 'asc') {
  return function compareFn(a: T, b: T, i = 0): number {
    const field = fields[i];

    if (field && a[field] === b[field]) {
      return compareFn(a, b, i + 1)
    }

    if (a[field] < b[field]) {
      return order === 'asc' ? -1 : 1;
    }

    if (a[field] > b[field]) {
      return order === 'asc' ? 1 : -1;
    }

    return 0;
  }
}

export function toPersianCharacter(value: string): string {
  if (!value) {
    return ''
  }

  const flags = {
    ك: 'ک',
    دِ: 'د',
    بِ: 'ب',
    زِ: 'ز',
    ذِ: 'ذ',
    شِ: 'ش',
    سِ: 'س',
    ى: 'ی',
    ي: 'ی',
    '١': '۱',
    '٢': '۲',
    '٣': '۳',
    '٤': '۴',
    '٥': '۵',
    '٦': '۶',
    '٧': '۷',
    '٨': '۸',
    '٩': '۹',
    '٠': '۰',
  }

  const regex = new RegExp(Object.keys(flags).join('|'), 'g')
  const replaceArabicCharacter = value.replace(regex, (match) => {
    if (match in flags) {
      return `${flags[match as keyof typeof flags]}`
    }
    return match
  })

  return replaceArabicCharacter
}

export function objectFilter<T extends Object, K extends keyof T>(obj: T, predicate: (key: any) => boolean) {
  return (<K[]>Object.keys(obj))
    .filter(key => predicate(obj[key]))
    .reduce((res, key) => (res[key] = obj[key], res), <T>{});
}

export function objectRenameKey<T extends Object, K extends keyof T>(inputObj: T, oldKey: K, newKey: K) {
  const obj = { ...inputObj };

  if (obj.hasOwnProperty(oldKey) && oldKey !== newKey) {
    Object.defineProperty(obj, newKey,
      <K>Object.getOwnPropertyDescriptor(obj, oldKey));
    delete obj[oldKey];
  }
  return obj;
}

export function isObject(item: any) {
  return (item && typeof item === 'object' && !Array.isArray(item));
}

export function mergeDeep<T extends Object>(target: T, ...sources: T[]): T {
  if (!sources.length) return target;
  const source = sources.shift();

  if (isObject(target) && isObject(source)) {
    for (const key in source) {
      if (isObject(source[key])) {
        if (!target[key]) Object.assign(target, { [key]: {} });
        mergeDeep(target[key] || <T>{}, source[key] || <T>{});
      } else {
        Object.assign(target, { [key]: source[key] });
      }
    }
  }

  return mergeDeep(target, ...sources);
}

export function generateDayWiseTimeSeries(startDate: number, count: number, range: { min: number, max: number }) {
  var i = 0;
  var series = [];
  while (i < count) {
    var x = startDate;
    var y = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;

    series.push([x, y]);
    startDate += 86400000;
    i++;
  }
  return series;
}

export function flattenControls(form: AbstractControl): AbstractControl[] {
  let extracted: AbstractControl[] = [form];
  if (form instanceof FormArray || form instanceof FormGroup) {
    const children = Object.values(form.controls).map(flattenControls);
    extracted = extracted.concat(...children);
  }
  return extracted;
}
